import React, { Component } from 'react';
import {
    SofaFormControl, SofaLink
} from '@sofology/sofologycomponents';
import { Grid } from '@material-ui/core';

import './FinanceContactDetailsForm.scss';
import { SofaFormComponent } from 'interfaces';
import {
    IlpQuestionDescriptions,
    IfcApplicationData,
    SofaFormCrossValidationHandlerType
} from 'models';
import {
    componentGeneratorHelper,
    formGeneratorHelper,
    getAddressComponent,
    PropertyValueType,
    SofaFormType,
    SofologyComponentsToIlpQuestionaryNames
} from 'helpers/dynamicFormHelpers';

const CUSTOMER_CONTACT_FORM_KEY = 'address';

interface OwnProps {
    ifcQuestionSet: IlpQuestionDescriptions;
    ifcApplicationData: IfcApplicationData;
    ref?: React.RefObject<FinanceContactDetailsFormType>;
    onGoToStartCheckout: () => void;
    onDynamicFormInitialized: () => void;
}

interface OwnState {
    contactDetailsForm: SofaFormType;
    proccessFormCrossValidation: SofaFormCrossValidationHandlerType;
    formHasInvalidReadonlyControls: boolean;
}

class FinanceContactDetailsForm extends Component<OwnProps, OwnState> implements SofaFormComponent<Record<string, PropertyValueType>> {
    constructor(props: OwnProps) {
        super(props);

        let contactDetailsForm = formGeneratorHelper.createSofaForm(
            'contactDetailsForm',
            this.props.ifcQuestionSet[CUSTOMER_CONTACT_FORM_KEY].properties,
            (updateStateFunc: any) => this.setState(updateStateFunc),
            () => this.state,
            this.props.ifcApplicationData,
        );

        this.state = {
            contactDetailsForm: contactDetailsForm.form,
            proccessFormCrossValidation: contactDetailsForm.crossValidationHandler,
            formHasInvalidReadonlyControls: false,
        };
    }

    componentDidMount(): void {
        formGeneratorHelper.validateReadonlyControls(this.props.ifcQuestionSet[CUSTOMER_CONTACT_FORM_KEY].properties, this.state.contactDetailsForm)
            .then(invalidControls => {
                if (invalidControls.length) {
                    this.setState({ formHasInvalidReadonlyControls: true }, () => {
                        this.props.onDynamicFormInitialized();
                    });
                }
            }).catch();
    }

    componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>): void {
        this.state.proccessFormCrossValidation(prevState, this.state);
    }

    render(): JSX.Element {
        const { ifcQuestionSet, ifcApplicationData } = this.props;
        const { formHasInvalidReadonlyControls } = this.state;
        const customerFieldSet = ifcQuestionSet[CUSTOMER_CONTACT_FORM_KEY];

        const formComponents = componentGeneratorHelper.getComponents(this.state.contactDetailsForm, customerFieldSet.properties, ifcApplicationData);
        const currentAddressComponent = formComponents.find(component => component.key == SofologyComponentsToIlpQuestionaryNames.BillingAddress);
        const deliveryAddressComponent = this.createDeliveryAddressComponent(formComponents, currentAddressComponent);

        let regularRenderComponents = formComponents.filter(component => component.key !== SofologyComponentsToIlpQuestionaryNames.BillingAddress && component.key !== SofologyComponentsToIlpQuestionaryNames.DeliveryAddress);
        this.addAddressHistoryIsRequiredNotification(regularRenderComponents);

        return (
            <div className='finance-contact-details-form'>
                <div className='validation-scroll-container'>
                    {formHasInvalidReadonlyControls && this.renderInvalidReadonlytFieldMessage()}
                    <h6 className='finance-contact-details-form__title'> {customerFieldSet.legend}</h6>
                    <div className='finance-contact-details-form__readonly-address'>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Grid container spacing={3}>
                                    {currentAddressComponent}
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container spacing={3}>
                                    {deliveryAddressComponent}
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                    <div className='finance-contact-details-form__dynamic-form'>
                        <Grid container spacing={3}>
                            {regularRenderComponents}
                        </Grid>
                    </div>
                </div>
            </div>
        );
    }

    renderInvalidReadonlytFieldMessage(): JSX.Element {
        return (
            <Grid item xs={12}>
                <div className='finance-contact-details-form__readonly-fields-error'>
                    Previously saved information is missing mandatory fields or contains invalid characters.
                    <SofaLink onClick={this.props.onGoToStartCheckout} text='Please go back and enter manually' />
                </div>
            </Grid>
        );
    }

    addAddressHistoryIsRequiredNotification(components: JSX.Element[]) {
        const notificationElement = (
            <Grid key='address-history-notification' item xs={12}>
                <div className='finance-contact-details-form__address-required-notification'>
                    3 years of previous address history is required
                </div>
            </Grid>
        );

        const previousAddressIndex = components.findIndex(component => component.key === SofologyComponentsToIlpQuestionaryNames.FirstPreviousAddress);
        if (previousAddressIndex !== -1) {
            components.splice(previousAddressIndex, 0, notificationElement);
        }
    }

    // TODO: check if it's possible to not display delivery address when it's the same as billing
    // while we have to display - this hack :((
    // cannot just render the same component (of billing address) twice because they will have the same label
    createDeliveryAddressComponent(formComponents: JSX.Element[], billingAddressComponent: JSX.Element | undefined): JSX.Element | null {
        let deliveryAddressComponent = formComponents.find(component => component.key == SofologyComponentsToIlpQuestionaryNames.DeliveryAddress);

        if (deliveryAddressComponent) {
            return deliveryAddressComponent;
        }

        const billingAddressControl = this.state.contactDetailsForm.controls['customer.address.currentAddress'];
        const billingAddressProperty = this.props.ifcQuestionSet[CUSTOMER_CONTACT_FORM_KEY].properties.currentAddress;

        if (!billingAddressComponent
            || !billingAddressControl
            || !billingAddressProperty) {
            return null;
        }

        deliveryAddressComponent = getAddressComponent(
            'Your delivery address:',
            billingAddressControl as any,
            billingAddressProperty as any,
            this.props.ifcApplicationData,
            true);

        return deliveryAddressComponent;
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        return this.state.contactDetailsForm.validate();
    };

    public getFormData = (): Record<string, PropertyValueType> => {
        return formGeneratorHelper.extractFormDataToIlpObject(
            this.state.contactDetailsForm,
            this.props.ifcQuestionSet[CUSTOMER_CONTACT_FORM_KEY].properties);
    };

    public hasInvalidReadonlyControls = (): boolean => {
        return this.state.formHasInvalidReadonlyControls;
    };
}

export default (FinanceContactDetailsForm);
export type FinanceContactDetailsFormType = FinanceContactDetailsForm;